/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { createContext, useState, useMemo, useEffect } from 'react';
import i18n, { changeLanguage as applyLanguage, odooLangToI18n } from 'src/localization/i18n';
import { useProfile } from 'src/utils/ProfileService';

interface LocalizationProviderProps {
  children: React.ReactNode;
}

export const LocalizationContext = createContext<{
  changeLanguage: (language: string) => void;
}>({
  changeLanguage: () => {},
});

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<string>(localStorage.getItem('language') || 'en');

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const value = useMemo(() => ({ changeLanguage }), []);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

/**
 * Applies the backend-stored language preference app-wide. Mirrors ThemeSync:
 * kept separate from LocalizationProvider so the provider stays usable without a
 * ProfileProvider ancestor. Render inside ProfileProvider.
 *
 * Guarded on `profile.id` so it only acts on a real, loaded profile — never on
 * the placeholder (which carries `de_DE`) or after logout, where the login
 * screen keeps whatever language the device last used.
 */
export const LanguageSync: React.FC = () => {
  const { profile } = useProfile();

  useEffect(() => {
    if (profile?.id && profile.lang) {
      applyLanguage(odooLangToI18n(profile.lang));
    }
  }, [profile?.id, profile?.lang]);

  return null;
};
